(() => {
  if (customElements.get('myfihu-smart-reviews')) return;

  const PRODUCT_CACHE_TTL = 24 * 60 * 60 * 1000;
  const REVIEW_CACHE_TTL = 10 * 60 * 1000;
  const SHOPIFY_PAGE_SIZE = 250;
  const LOOX_PAGE_SIZE = 250;
  const MAX_LOOX_PAGES_PER_LOAD = 4;
  const SHOPIFY_API_VERSION = '2026-07';

  class MyFiHuSmartReviews extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.config = this.readConfig();
      this.grid = this.querySelector('[data-myfihu-reviews-grid]');
      this.status = this.querySelector('.myfihu-reviews__status');
      this.statusText = this.querySelector('[data-myfihu-reviews-status]');
      this.actions = this.querySelector('[data-myfihu-reviews-actions]');
      this.moreButton = this.querySelector('[data-myfihu-reviews-more]');
      this.retryButton = this.querySelector('[data-myfihu-reviews-retry]');
      this.productIds = new Set();
      this.seenReviewIds = new Set();
      this.reviewBuffer = [];
      this.nextLooxUrl = null;
      this.loading = false;
      this.exhausted = false;
      this.masonryFrame = null;
      this.handleMasonryResize = () => this.scheduleMasonry();
      this.modal = this.createMediaModal();
      document.body.appendChild(this.modal);

      window.addEventListener('resize', this.handleMasonryResize, { passive: true });
      if ('ResizeObserver' in window) {
        this.masonryObserver = new ResizeObserver(() => this.scheduleMasonry());
      }

      this.moreButton?.addEventListener('click', () => this.loadNextBatch(true));
      this.retryButton?.addEventListener('click', () => {
        if (this.productIds.size && this.nextLooxUrl) this.loadNextBatch(true);
        else this.initializeReviews();
      });
      this.scheduleInitialLoad();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleMasonryResize);
      this.masonryObserver?.disconnect();
      if (this.masonryFrame) window.cancelAnimationFrame(this.masonryFrame);
      if (this.modal?.open) this.closeMediaModal(false);
      this.modal?.remove();
    }

    readConfig() {
      const node = this.querySelector('[data-myfihu-reviews-config]');
      if (!node) return {};

      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        return {};
      }
    }

    scheduleInitialLoad() {
      const observe = () => {
        if (!('IntersectionObserver' in window)) {
          this.runWhenIdle(() => this.initializeReviews());
          return;
        }

        const observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            observer.disconnect();
            this.runWhenIdle(() => this.initializeReviews());
          },
          { rootMargin: '300px 0px' }
        );

        observer.observe(this);
      };

      if (document.readyState === 'complete') observe();
      else window.addEventListener('load', observe, { once: true });
    }

    runWhenIdle(callback) {
      if ('requestIdleCallback' in window) window.requestIdleCallback(callback, { timeout: 1200 });
      else window.setTimeout(callback, 120);
    }

    async initializeReviews() {
      if (!this.config.publicStoreId || !this.config.shopDomain || !this.config.collectionHandles?.length) {
        this.showError(this.config.errorMessage);
        return;
      }

      try {
        this.setLoading(true);
        this.productIds = await this.getDogProductIds();

        if (!this.productIds.size) {
          this.showEmpty();
          return;
        }

        const base = `https://storefront-api.loox.io/storefront/v1/store/${encodeURIComponent(
          this.config.publicStoreId
        )}/product-reviews`;
        this.nextLooxUrl = `${base}?status=published&sort=featured&limit=${LOOX_PAGE_SIZE}`;
        this.setLoading(false);
        await this.loadNextBatch();
      } catch (error) {
        this.showError(this.config.errorMessage, true);
      } finally {
        this.setLoading(false);
      }
    }

    async getDogProductIds() {
      const handles = [...new Set(this.config.collectionHandles.filter(Boolean))].sort();
      const cacheKey = `myfihu-dog-product-ids:v1:${this.config.shopDomain}:${handles.join(',')}`;
      const cached = this.readCache(this.getStorage('localStorage'), cacheKey, PRODUCT_CACHE_TTL);
      if (cached?.ids?.length) return new Set(cached.ids.map(String));

      const allIds = new Set();
      let queueIndex = 0;
      const workerCount = Math.min(2, handles.length);

      const worker = async () => {
        while (queueIndex < handles.length) {
          const currentIndex = queueIndex;
          queueIndex += 1;
          const ids = await this.fetchCollectionProductIds(handles[currentIndex]);
          ids.forEach((id) => allIds.add(String(id)));
        }
      };

      await Promise.all(Array.from({ length: workerCount }, () => worker()));
      this.writeCache(this.getStorage('localStorage'), cacheKey, { ids: [...allIds] });
      return allIds;
    }

    async fetchCollectionProductIds(handle) {
      const ids = [];
      let cursor = null;
      let hasNextPage = true;
      const query = `
        query MyFiHuDogCollection($handle: String!, $first: Int!, $after: String) {
          collection(handle: $handle) {
            products(first: $first, after: $after) {
              nodes { id }
              pageInfo { hasNextPage endCursor }
            }
          }
        }
      `;

      while (hasNextPage) {
        const response = await fetch(
          `${window.location.origin}/api/${SHOPIFY_API_VERSION}/graphql.json`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              query,
              variables: { handle, first: SHOPIFY_PAGE_SIZE, after: cursor },
            }),
          }
        );

        if (!response.ok) throw new Error(`Shopify collection request failed: ${response.status}`);
        const payload = await response.json();
        if (payload.errors?.length) throw new Error('Shopify collection query returned errors.');

        const products = payload.data?.collection?.products;
        if (!products) break;
        products.nodes.forEach((product) => {
          const id = this.normalizeProductId(product.id);
          if (id) ids.push(id);
        });

        hasNextPage = Boolean(products.pageInfo?.hasNextPage);
        cursor = products.pageInfo?.endCursor || null;
      }

      return ids;
    }

    async loadNextBatch(preserveScroll = false) {
      if (this.loading) return;
      this.setLoading(true);
      this.hideRetry();

      try {
        const target = Number(this.config.reviewsPerLoad) || 20;
        let pagesScanned = 0;

        while (this.reviewBuffer.length < target && this.nextLooxUrl && pagesScanned < MAX_LOOX_PAGES_PER_LOAD) {
          const page = await this.fetchLooxPage(this.nextLooxUrl);
          pagesScanned += 1;

          const reviews = Array.isArray(page.reviews) ? page.reviews : [];
          reviews.forEach((review) => {
            const reviewId = String(review.id || '');
            const productId = this.normalizeProductId(review.product?.id);
            const rating = Number(review.rating || 0);

            if (!reviewId || this.seenReviewIds.has(reviewId)) return;
            this.seenReviewIds.add(reviewId);
            if (rating < 4 || !productId || !this.productIds.has(productId)) return;
            this.reviewBuffer.push(review);
          });

          const pagination = page.pagination || {};
          this.nextLooxUrl = pagination.hasMore && pagination.nextPageUrl
            ? new URL(pagination.nextPageUrl, 'https://storefront-api.loox.io').toString()
            : null;
        }

        // Preserve Loox's featured order inside each group while ensuring visual
        // reviews are displayed before text-only reviews in the current batch.
        const visualReviews = [];
        const textReviews = [];
        this.reviewBuffer.forEach((review) => {
          (this.getReviewMedia(review) ? visualReviews : textReviews).push(review);
        });
        this.reviewBuffer = [...visualReviews, ...textReviews];

        const batch = this.reviewBuffer.splice(0, target);
        if (batch.length) {
          const scrollPosition = preserveScroll
            ? { left: window.scrollX, top: window.scrollY }
            : null;
          this.renderReviews(batch);
          if (scrollPosition) this.restoreScrollPosition(scrollPosition);
        }

        this.exhausted = !this.nextLooxUrl && this.reviewBuffer.length === 0;
        this.updateControls();

        if (!this.grid.children.length && this.exhausted) this.showEmpty();
        else this.hideStatus();
      } catch (error) {
        this.showError(this.config.errorMessage, true);
      } finally {
        this.setLoading(false);
      }
    }

    async fetchLooxPage(url) {
      const cacheKey = `myfihu-loox-page:v1:${url}`;
      const cached = this.readCache(this.getStorage('sessionStorage'), cacheKey, REVIEW_CACHE_TTL);
      if (cached?.reviews && cached?.pagination) return cached;

      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Loox request failed: ${response.status}`);
      const payload = await response.json();
      this.writeCache(this.getStorage('sessionStorage'), cacheKey, payload);
      return payload;
    }

    renderReviews(reviews) {
      const fragment = document.createDocumentFragment();
      const items = reviews.map((review) => this.createReviewCard(review));
      items.forEach((item) => fragment.appendChild(item));
      this.grid.appendChild(fragment);
      items.forEach((item) => {
        this.masonryObserver?.observe(item);
        item.querySelectorAll('img').forEach((image) => {
          if (!image.complete) image.addEventListener('load', () => this.scheduleMasonry(), { once: true });
        });
      });
      this.scheduleMasonry();
    }

    scheduleMasonry() {
      if (this.masonryFrame) return;
      this.masonryFrame = window.requestAnimationFrame(() => {
        this.masonryFrame = null;
        this.layoutMasonry();
      });
    }

    restoreScrollPosition(position) {
      window.requestAnimationFrame(() => {
        window.scrollTo({ ...position, behavior: 'auto' });
        window.requestAnimationFrame(() => {
          window.scrollTo({ ...position, behavior: 'auto' });
        });
      });
    }

    layoutMasonry() {
      if (!this.grid?.children.length) return;

      const styles = window.getComputedStyle(this.grid);
      const rowHeight = Number.parseFloat(styles.gridAutoRows);
      const rowGap = Number.parseFloat(styles.rowGap);
      if (!rowHeight || Number.isNaN(rowGap)) return;

      Array.from(this.grid.children).forEach((item) => {
        const height = item.getBoundingClientRect().height;
        const span = Math.max(1, Math.ceil((height + rowGap) / (rowHeight + rowGap)));
        const nextValue = `span ${span}`;
        if (item.style.gridRowEnd !== nextValue) item.style.gridRowEnd = nextValue;
      });
    }

    createReviewCard(review) {
      const item = document.createElement('li');
      const article = document.createElement('article');
      article.className = 'myfihu-review-card';

      const media = this.getReviewMedia(review);
      const rawImageUrl = media?.type === 'video'
        ? media.thumbnailUrl
        : media?.url || media?.thumbnailUrl;
      const imageUrl = this.safeUrl(rawImageUrl);
      const productUrl = this.safeUrl(review.product?.url);
      const productImageUrl = this.safeUrl(review.product?.imageUrl);

      if (imageUrl) {
        const mediaElement = document.createElement('button');
        mediaElement.type = 'button';
        mediaElement.className = 'myfihu-review-card__media';
        mediaElement.setAttribute(
          'aria-label',
          media?.type === 'video' ? 'Open review video' : 'Open review image'
        );
        mediaElement.addEventListener('click', () => this.openMediaModal(review, mediaElement));

        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = '';
        image.loading = 'lazy';
        image.decoding = 'async';
        image.width = 480;
        image.height = 360;
        mediaElement.appendChild(image);

        if (media?.type === 'video') {
          const badge = document.createElement('span');
          badge.className = 'myfihu-review-card__video-badge';
          badge.setAttribute('aria-hidden', 'true');
          mediaElement.appendChild(badge);
        }

        article.appendChild(mediaElement);
      }

      const content = document.createElement('div');
      content.className = 'myfihu-review-card__content';
      content.appendChild(this.createRating(Number(review.rating || 0)));

      if (review.body) {
        const body = document.createElement('p');
        body.className = 'myfihu-review-card__body';
        body.textContent = review.body;
        content.appendChild(body);
      }

      const meta = document.createElement('div');
      meta.className = 'myfihu-review-card__meta';
      const reviewerWrap = document.createElement('div');
      const reviewer = document.createElement('span');
      reviewer.className = 'myfihu-review-card__reviewer';
      reviewer.textContent = review.reviewer?.name || 'Customer';
      reviewerWrap.appendChild(reviewer);

      if (review.verified) {
        const verified = document.createElement('span');
        verified.className = 'myfihu-review-card__verified';
        verified.textContent = this.config.verifiedLabel;
        reviewerWrap.appendChild(verified);
      }

      meta.appendChild(reviewerWrap);
      const formattedDate = this.formatDate(review.date || review.createdAt);
      if (formattedDate) {
        const date = document.createElement('time');
        date.dateTime = review.date || review.createdAt;
        date.textContent = formattedDate;
        meta.appendChild(date);
      }
      content.appendChild(meta);

      if (review.product?.name || productImageUrl) {
        const product = productUrl ? document.createElement('a') : document.createElement('div');
        product.className = 'myfihu-review-card__product';
        if (productUrl) product.href = productUrl;

        if (productImageUrl) {
          const productImage = document.createElement('img');
          productImage.className = 'myfihu-review-card__product-image';
          productImage.src = productImageUrl;
          productImage.alt = '';
          productImage.loading = 'lazy';
          productImage.decoding = 'async';
          productImage.width = 56;
          productImage.height = 56;
          product.appendChild(productImage);
        }

        if (review.product?.name) {
          const productName = document.createElement('span');
          productName.className = 'myfihu-review-card__product-name';
          productName.textContent = review.product.name;
          product.appendChild(productName);
        }

        content.appendChild(product);
      }

      article.appendChild(content);
      item.appendChild(article);
      return item;
    }

    createRating(rating) {
      const wrapper = document.createElement('span');
      const rounded = Math.max(0, Math.min(5, Math.round(rating)));
      wrapper.className = 'myfihu-review-card__rating';
      wrapper.setAttribute('aria-label', `${rating} ${this.config.outOfFiveLabel}`);

      for (let index = 1; index <= 5; index += 1) {
        const star = document.createElement('span');
        star.textContent = '★';
        star.setAttribute('aria-hidden', 'true');
        if (index > rounded) star.className = 'myfihu-review-card__star--empty';
        wrapper.appendChild(star);
      }
      return wrapper;
    }

    normalizeProductId(value) {
      if (value === null || value === undefined) return '';
      return String(value).split('/').pop().trim();
    }

    getReviewMedia(review) {
      return this.getReviewMediaItems(review)[0] || null;
    }

    getReviewMediaItems(review) {
      if (!Array.isArray(review?.media)) return [];

      return review.media.reduce((items, item) => {
        if (!item) return items;
        const rawType = String(item.type || '').toLowerCase();
        const rawUrl = typeof item.url === 'string' ? item.url.trim() : '';
        const rawEmbed = item.embedUrl
          || item.embed_url
          || item.iframeUrl
          || item.iframe_url
          || item.iframe
          || item.embed
          || item.html
          || '';
        const type = rawType.includes('video') || rawType.includes('iframe') || rawType.includes('embed')
          ? 'video'
          : 'image';
        const markupEmbedUrl = this.extractIframeUrl(rawEmbed) || this.extractIframeUrl(rawUrl);
        const resolvedUrl = rawUrl.startsWith('<') ? '' : this.safeUrl(rawUrl);
        const embedUrl = markupEmbedUrl || (
          type === 'video'
            && resolvedUrl
            && (this.isVideoEmbedUrl(resolvedUrl) || !this.isDirectVideoUrl(resolvedUrl))
            ? resolvedUrl
            : ''
        );
        const url = embedUrl ? '' : resolvedUrl;
        const thumbnailUrl = this.safeUrl(item.thumbnailUrl || item.thumbnail_url);
        if (type === 'video' && !url && !embedUrl && !thumbnailUrl) return items;
        if (type === 'image' && !url && !thumbnailUrl) return items;
        items.push({ type, url, embedUrl, thumbnailUrl });
        return items;
      }, []);
    }

    extractIframeUrl(value) {
      if (typeof value !== 'string' || !value.trim().startsWith('<')) return '';
      try {
        const documentFragment = new DOMParser().parseFromString(value, 'text/html');
        return this.safeUrl(documentFragment.querySelector('iframe')?.getAttribute('src'));
      } catch (error) {
        return '';
      }
    }

    isVideoEmbedUrl(value) {
      if (!value) return false;
      try {
        const url = new URL(value);
        const hostname = url.hostname.toLowerCase();
        return hostname.includes('youtube.com')
          || hostname === 'youtu.be'
          || hostname.includes('vimeo.com')
          || url.pathname.includes('/embed/');
      } catch (error) {
        return false;
      }
    }

    isDirectVideoUrl(value) {
      if (!value) return false;
      try {
        const pathname = new URL(value).pathname.toLowerCase();
        return /\.(mp4|m4v|webm|ogv|ogg|mov|m3u8)$/.test(pathname);
      } catch (error) {
        return false;
      }
    }

    addEmbedAutoplayParameters(value) {
      try {
        const url = new URL(value);
        url.searchParams.set('autoplay', '1');
        url.searchParams.set('mute', '1');
        url.searchParams.set('muted', '1');
        url.searchParams.set('playsinline', '1');
        return url.toString();
      } catch (error) {
        return value;
      }
    }

    createMediaModal() {
      const modal = document.createElement('dialog');
      modal.className = 'myfihu-review-modal';
      modal.setAttribute('aria-label', 'Review media and details');

      const layout = document.createElement('div');
      layout.className = 'myfihu-review-modal__layout';

      const closeButton = document.createElement('button');
      closeButton.type = 'button';
      closeButton.className = 'myfihu-review-modal__close';
      closeButton.setAttribute('aria-label', 'Close review');
      closeButton.textContent = '×';

      const gallery = document.createElement('div');
      gallery.className = 'myfihu-review-modal__gallery';

      const stage = document.createElement('div');
      stage.className = 'myfihu-review-modal__stage';
      stage.setAttribute('aria-live', 'polite');

      const previousButton = document.createElement('button');
      previousButton.type = 'button';
      previousButton.className = 'myfihu-review-modal__nav myfihu-review-modal__nav--previous';
      previousButton.setAttribute('aria-label', 'Previous media');
      previousButton.textContent = '‹';

      const nextButton = document.createElement('button');
      nextButton.type = 'button';
      nextButton.className = 'myfihu-review-modal__nav myfihu-review-modal__nav--next';
      nextButton.setAttribute('aria-label', 'Next media');
      nextButton.textContent = '›';

      const counter = document.createElement('span');
      counter.className = 'myfihu-review-modal__counter';
      counter.setAttribute('aria-live', 'polite');

      const details = document.createElement('div');
      details.className = 'myfihu-review-modal__details';

      gallery.append(stage, previousButton, nextButton, counter);
      layout.append(gallery, details);
      modal.append(closeButton, layout);

      this.modalStage = stage;
      this.modalDetails = details;
      this.modalCounter = counter;
      this.modalPreviousButton = previousButton;
      this.modalNextButton = nextButton;
      this.modalCloseButton = closeButton;

      closeButton.addEventListener('click', () => this.closeMediaModal());
      previousButton.addEventListener('click', () => this.showAdjacentMedia(-1));
      nextButton.addEventListener('click', () => this.showAdjacentMedia(1));
      modal.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeMediaModal();
      });
      modal.addEventListener('click', (event) => {
        if (event.target === modal) this.closeMediaModal();
      });
      modal.addEventListener('keydown', (event) => this.handleModalKeydown(event));

      let touchStartX = 0;
      let touchStartY = 0;
      stage.addEventListener('touchstart', (event) => {
        const touch = event.changedTouches[0];
        touchStartX = touch?.clientX || 0;
        touchStartY = touch?.clientY || 0;
      }, { passive: true });
      stage.addEventListener('touchend', (event) => {
        if ((this.modalMedia?.length || 0) < 2) return;
        const touch = event.changedTouches[0];
        const deltaX = (touch?.clientX || 0) - touchStartX;
        const deltaY = (touch?.clientY || 0) - touchStartY;
        if (Math.abs(deltaX) < 60 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
        this.showAdjacentMedia(deltaX < 0 ? 1 : -1);
      }, { passive: true });

      return modal;
    }

    openMediaModal(review, opener) {
      const media = this.getReviewMediaItems(review);
      if (!media.length || !this.modal) return;

      this.modalReview = review;
      this.modalMedia = media;
      this.modalMediaIndex = 0;
      this.modalOpener = opener;
      this.renderModalDetails(review);
      this.lockPageScroll();

      if (typeof this.modal.showModal === 'function') this.modal.showModal();
      else this.modal.setAttribute('open', '');
      this.renderModalMedia();
      this.modalCloseButton.focus({ preventScroll: true });
    }

    closeMediaModal(restoreFocus = true) {
      if (!this.modal) return;
      this.stopModalVideo();
      if (typeof this.modal.close === 'function' && this.modal.open) this.modal.close();
      else this.modal.removeAttribute('open');
      this.unlockPageScroll();

      if (restoreFocus && this.modalOpener?.isConnected) {
        this.modalOpener.focus({ preventScroll: true });
      }
      this.modalStage?.replaceChildren();
      this.modalMedia = [];
      this.modalReview = null;
      this.modalOpener = null;
    }

    renderModalMedia() {
      const media = this.modalMedia?.[this.modalMediaIndex];
      if (!media || !this.modalStage) return;
      this.stopModalVideo();
      this.modalStage.replaceChildren();

      if (media.type === 'video' && media.embedUrl) {
        const iframe = document.createElement('iframe');
        iframe.className = 'myfihu-review-modal__media myfihu-review-modal__media--embed';
        iframe.src = this.addEmbedAutoplayParameters(media.embedUrl);
        iframe.title = `Review video ${this.modalMediaIndex + 1}`;
        iframe.allow = 'autoplay; fullscreen; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.loading = 'eager';
        iframe.referrerPolicy = 'strict-origin-when-cross-origin';
        iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-presentation');
        this.modalStage.appendChild(iframe);
      } else if (media.type === 'video' && media.url) {
        const video = document.createElement('video');
        video.className = 'myfihu-review-modal__media';
        video.src = media.url;
        if (media.thumbnailUrl) video.poster = media.thumbnailUrl;
        video.controls = true;
        video.autoplay = true;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.preload = 'metadata';
        video.setAttribute('aria-label', `Review video ${this.modalMediaIndex + 1}`);
        this.modalStage.appendChild(video);
        video.play().catch(() => {});
      } else {
        const imageUrl = media.url || media.thumbnailUrl;
        if (imageUrl) {
          const image = document.createElement('img');
          image.className = 'myfihu-review-modal__media';
          image.src = imageUrl;
          image.alt = `Review image ${this.modalMediaIndex + 1}`;
          image.decoding = 'async';
          this.modalStage.appendChild(image);
        }
      }

      const hasMultipleMedia = this.modalMedia.length > 1;
      this.modalPreviousButton.hidden = !hasMultipleMedia;
      this.modalNextButton.hidden = !hasMultipleMedia;
      this.modalCounter.hidden = !hasMultipleMedia;
      this.modalCounter.textContent = `${this.modalMediaIndex + 1} / ${this.modalMedia.length}`;
    }

    renderModalDetails(review) {
      this.modalDetails.replaceChildren();
      this.modalDetails.appendChild(this.createRating(Number(review.rating || 0)));

      if (review.body) {
        const body = document.createElement('p');
        body.className = 'myfihu-review-modal__body';
        body.textContent = review.body;
        this.modalDetails.appendChild(body);
      }

      const meta = document.createElement('div');
      meta.className = 'myfihu-review-modal__meta';
      const reviewerWrap = document.createElement('div');
      const reviewer = document.createElement('strong');
      reviewer.className = 'myfihu-review-modal__reviewer';
      reviewer.textContent = review.reviewer?.name || 'Customer';
      reviewerWrap.appendChild(reviewer);

      if (review.verified) {
        const verified = document.createElement('span');
        verified.className = 'myfihu-review-modal__verified';
        verified.textContent = this.config.verifiedLabel;
        reviewerWrap.appendChild(verified);
      }
      meta.appendChild(reviewerWrap);

      const rawDate = review.date || review.createdAt;
      const formattedDate = this.formatDate(rawDate);
      if (formattedDate) {
        const date = document.createElement('time');
        date.dateTime = rawDate;
        date.textContent = formattedDate;
        meta.appendChild(date);
      }
      this.modalDetails.appendChild(meta);

      const productUrl = this.safeUrl(review.product?.url);
      const productImageUrl = this.safeUrl(review.product?.imageUrl);
      if (review.product?.name || productImageUrl) {
        const product = productUrl ? document.createElement('a') : document.createElement('div');
        product.className = 'myfihu-review-modal__product';
        if (productUrl) product.href = productUrl;

        if (productImageUrl) {
          const image = document.createElement('img');
          image.src = productImageUrl;
          image.alt = '';
          image.loading = 'lazy';
          image.decoding = 'async';
          image.width = 72;
          image.height = 72;
          product.appendChild(image);
        }

        if (review.product?.name) {
          const name = document.createElement('span');
          name.textContent = review.product.name;
          product.appendChild(name);
        }
        this.modalDetails.appendChild(product);
      }
    }

    showAdjacentMedia(direction) {
      if ((this.modalMedia?.length || 0) < 2) return;
      this.modalMediaIndex = (
        this.modalMediaIndex + direction + this.modalMedia.length
      ) % this.modalMedia.length;
      this.renderModalMedia();
    }

    stopModalVideo() {
      const video = this.modalStage?.querySelector('video');
      if (!video) return;
      video.pause();
      video.removeAttribute('src');
      video.load();
    }

    handleModalKeydown(event) {
      if (event.key === 'ArrowLeft' && event.target?.tagName !== 'VIDEO') {
        event.preventDefault();
        this.showAdjacentMedia(-1);
        return;
      }
      if (event.key === 'ArrowRight' && event.target?.tagName !== 'VIDEO') {
        event.preventDefault();
        this.showAdjacentMedia(1);
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = Array.from(this.modal.querySelectorAll(
        'button:not([hidden]):not([disabled]), a[href], video[controls]'
      )).filter((element) => element.getClientRects().length > 0);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    lockPageScroll() {
      if (this.pageScrollState) return;
      const bodyStyle = document.body.style;
      this.pageScrollState = {
        left: window.scrollX,
        top: window.scrollY,
        htmlOverflow: document.documentElement.style.overflow,
        body: {
          position: bodyStyle.position,
          top: bodyStyle.top,
          left: bodyStyle.left,
          right: bodyStyle.right,
          width: bodyStyle.width,
          overflow: bodyStyle.overflow,
        },
      };

      document.documentElement.style.overflow = 'hidden';
      bodyStyle.position = 'fixed';
      bodyStyle.top = `-${this.pageScrollState.top}px`;
      bodyStyle.left = `-${this.pageScrollState.left}px`;
      bodyStyle.right = '0';
      bodyStyle.width = '100%';
      bodyStyle.overflow = 'hidden';
    }

    unlockPageScroll() {
      if (!this.pageScrollState) return;
      const state = this.pageScrollState;
      const bodyStyle = document.body.style;
      document.documentElement.style.overflow = state.htmlOverflow;
      Object.assign(bodyStyle, state.body);
      this.pageScrollState = null;
      window.scrollTo({ left: state.left, top: state.top, behavior: 'auto' });
    }

    safeUrl(value) {
      if (!value) return '';
      try {
        const url = new URL(value, window.location.origin);
        return ['http:', 'https:'].includes(url.protocol) ? url.toString() : '';
      } catch (error) {
        return '';
      }
    }

    formatDate(value) {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';

      try {
        return new Intl.DateTimeFormat(this.config.locale || 'en', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        }).format(date);
      } catch (error) {
        return date.toLocaleDateString();
      }
    }

    setLoading(isLoading) {
      this.loading = isLoading;
      if (this.moreButton) this.moreButton.disabled = isLoading;
      if (this.retryButton) this.retryButton.disabled = isLoading;

      if (isLoading && !this.grid?.children.length) {
        this.status.hidden = false;
        this.statusText.textContent = this.config.loadingLabel;
        this.status.querySelector('.myfihu-reviews__spinner')?.removeAttribute('hidden');
      }
    }

    updateControls() {
      const canLoadMore = this.reviewBuffer.length > 0 || Boolean(this.nextLooxUrl);
      this.actions.hidden = !canLoadMore;
      this.moreButton.hidden = !canLoadMore;
      this.retryButton.hidden = true;
    }

    showError(message, canRetry = false) {
      this.status.hidden = false;
      this.statusText.textContent = message || this.config.errorMessage;
      this.status.querySelector('.myfihu-reviews__spinner')?.setAttribute('hidden', '');
      this.actions.hidden = !canRetry;
      this.moreButton.hidden = true;
      this.retryButton.hidden = !canRetry;
    }

    showEmpty() {
      this.status.hidden = false;
      this.statusText.textContent = this.config.emptyMessage;
      this.status.querySelector('.myfihu-reviews__spinner')?.setAttribute('hidden', '');
      this.actions.hidden = true;
    }

    hideStatus() {
      this.status.hidden = true;
    }

    hideRetry() {
      if (this.retryButton) this.retryButton.hidden = true;
    }

    getStorage(name) {
      try {
        return window[name];
      } catch (error) {
        return null;
      }
    }

    readCache(storage, key, ttl) {
      if (!storage) return null;
      try {
        const raw = storage.getItem(key);
        if (!raw) return null;
        const cached = JSON.parse(raw);
        if (!cached.savedAt || Date.now() - cached.savedAt > ttl) {
          storage.removeItem(key);
          return null;
        }
        return cached.value;
      } catch (error) {
        return null;
      }
    }

    writeCache(storage, key, value) {
      if (!storage) return;
      try {
        storage.setItem(key, JSON.stringify({ savedAt: Date.now(), value }));
      } catch (error) {
        // Storage can be unavailable in private browsing; the section still works without caching.
      }
    }
  }

  customElements.define('myfihu-smart-reviews', MyFiHuSmartReviews);
})();
